import { useUser } from "@clerk/clerk-react";
import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import { format } from "date-fns";
import {
  Calendar,
  ChevronLeft,
  Download,
  Loader2,
  MoreVertical,
  Search,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "@/components/admin/DataTable";
import { ConversionCount, FreemiumBadge } from "@/components/admin/FreemiumBadge";
import { PaymentStatusCell } from "@/components/admin/PaymentStatusCell";
import { SearchInput } from "@/components/admin/SearchInput";
import { StatusBadge } from "@/components/admin/StatusBadge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  type EnrollmentSort,
  useAvailableUsersForProgramme,
  useEnrollUser,
  useProgramme,
  useProgrammeEnrollments,
  useRemoveUser,
  useUpdateEnrollmentStatus,
} from "@/hooks/admin/programmes";
import { AdminLayout } from "@/layouts/AdminLayout";
import { isSuperadminRole } from "@/lib/roles";
import { api } from "@/services/api";
import type { EnrollmentWithUser } from "@/types";

const PAGE_SIZE = 20;

// Backend sorts by these enrollment columns; map TanStack column ids onto those.
const SORTABLE = new Set(["username", "role", "enrolled_at", "status", "current_week"]);

function statusTone(status: string): "success" | "info" | "neutral" {
  switch (status) {
    case "active":
      return "success";
    case "completed":
      return "info";
    default:
      return "neutral";
  }
}

export default function ProgrammeUsersPage() {
  const { programmeId } = useParams<{ programmeId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const isSuperadmin = isSuperadminRole(user?.publicMetadata?.role as string | undefined);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "enrolled_at", desc: true }]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const sort = sorting[0];

  const programmeQuery = useProgramme(programmeId);
  const programme = programmeQuery.data ?? null;
  const programmeLoading = programmeQuery.isPending;

  // Enrollment cutoff (KC-285/KC-291): once the cutoff passes the backend enroll
  // endpoint returns 403 for admins (superadmin bypasses via policy.enforce), so
  // hide the affordance to match. Superadmin keeps it as an explicit override.
  const enrollmentClosed =
    programme?.enrollment_cutoff != null && new Date(programme.enrollment_cutoff) <= new Date();
  const canEnrol = isSuperadmin || !enrollmentClosed;

  const availableUsersQuery = useAvailableUsersForProgramme(programmeId);
  const availableUsers = availableUsersQuery.data ?? [];

  const enrollmentsQuery = useProgrammeEnrollments(programmeId, {
    page,
    page_size: PAGE_SIZE,
    sort_by: (sort?.id ?? "enrolled_at") as EnrollmentSort,
    sort_order: sort ? (sort.desc ? "desc" : "asc") : "desc",
    q: search || undefined,
  });
  const data = enrollmentsQuery.data ?? null;
  const error = enrollmentsQuery.isError
    ? enrollmentsQuery.error instanceof Error
      ? enrollmentsQuery.error.message
      : "Failed to load participants"
    : null;

  const enrollUser = useEnrollUser();
  const { mutateAsync: mutateEnrollUser, isPending: isAdding } = enrollUser;
  const { mutateAsync: mutateRemoveUser } = useRemoveUser();
  const { mutateAsync: mutateUpdateStatus } = useUpdateEnrollmentStatus();

  // PaymentStatusCell expects an async refresh; invalidation drives the actual
  // refetch, so just await the in-flight queries afterwards.
  const refreshEnrollments = useCallback(async () => {
    await enrollmentsQuery.refetch();
  }, [enrollmentsQuery.refetch]);

  const handleAddUser = async (userId: string) => {
    if (!programmeId) return;
    try {
      await mutateEnrollUser({ programmeId, userId });
      setShowAddUserModal(false);
      setSearchQuery("");
    } catch (err) {
      console.error("Failed to add user:", err);
    }
  };

  const handleRemoveUser = useCallback(
    async (enrollmentId: string) => {
      if (!programmeId) return;
      try {
        await mutateRemoveUser({ programmeId, enrollmentId });
      } catch (err) {
        console.error("Failed to remove user:", err);
      }
    },
    [programmeId, mutateRemoveUser]
  );

  const handleStatusChange = useCallback(
    async (enrollmentId: string, newStatus: string) => {
      if (!programmeId) return;
      try {
        await mutateUpdateStatus({
          programmeId,
          enrollmentId,
          status: newStatus as "active" | "completed" | "dropped",
        });
      } catch (err) {
        console.error("Failed to update status:", err);
      }
    },
    [programmeId, mutateUpdateStatus]
  );

  const handleExportCSV = async () => {
    if (!programmeId) return;
    setIsExporting(true);
    try {
      const result = await api.exportCohortUsersCSV(programmeId);
      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export CSV:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const filteredAvailableUsers = availableUsers.filter((user) =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadgeColor = (role: string) =>
    role === "admin" ? "bg-purple-100 text-purple-800" : "bg-blue-100 text-blue-800";

  const columns = useMemo<ColumnDef<EnrollmentWithUser>[]>(
    () => [
      {
        accessorKey: "username",
        header: "Participant",
        cell: ({ row }) => (
          <Link
            to={`/admin/users/${row.original.user_id}`}
            className="font-medium text-gray-900 transition-colors hover:text-green-600"
          >
            {row.original.username}
          </Link>
        ),
      },
      {
        accessorKey: "role",
        header: "Role",
        cell: ({ row }) => {
          const isAdmin = String(row.original.role).toLowerCase() === "admin";
          return (
            <StatusBadge tone={isAdmin ? "info" : "neutral"}>
              {isAdmin ? "Admin" : "Participant"}
            </StatusBadge>
          );
        },
      },
      {
        accessorKey: "enrolled_at",
        header: "Enrolled",
        cell: ({ row }) => (
          <span className="text-sm text-gray-500">
            {format(new Date(row.original.enrolled_at), "MMM d, yyyy")}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => (
          <StatusBadge tone={statusTone(row.original.status)}>
            {row.original.status.toLowerCase()}
          </StatusBadge>
        ),
      },
      {
        accessorKey: "payment_status",
        header: "Payment",
        enableSorting: false,
        cell: ({ row }) =>
          row.original.payment_status ? (
            <PaymentStatusCell enrollment={row.original} onRefresh={refreshEnrollments} />
          ) : (
            <span className="text-xs text-gray-400">Free</span>
          ),
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const enrollment = row.original;
          return (
            <div className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded-lg p-2 transition-colors hover:bg-gray-100"
                  >
                    <MoreVertical className="h-4 w-4 text-gray-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {enrollment.status !== "active" && (
                    <DropdownMenuItem onClick={() => handleStatusChange(enrollment.id, "active")}>
                      Set Active
                    </DropdownMenuItem>
                  )}
                  {enrollment.status !== "completed" && (
                    <DropdownMenuItem
                      onClick={() => handleStatusChange(enrollment.id, "completed")}
                    >
                      Set Completed
                    </DropdownMenuItem>
                  )}
                  {enrollment.status !== "dropped" && (
                    <DropdownMenuItem onClick={() => handleStatusChange(enrollment.id, "dropped")}>
                      Set Dropped
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => handleRemoveUser(enrollment.id)}
                    className="text-red-600 focus:text-red-600"
                  >
                    Remove from Cohort
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [refreshEnrollments, handleStatusChange, handleRemoveUser]
  );

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const valid = next.filter((s) => SORTABLE.has(s.id));
      setPage(1);
      return valid.length ? valid : prev;
    });
  };

  if (programmeLoading) {
    return (
      <AdminLayout>
        <div className="flex h-full items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (!programme) {
    return (
      <AdminLayout>
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="mb-4 text-lg text-gray-900">Cohort not found</p>
            <button
              type="button"
              onClick={() => navigate("/admin/programmes")}
              className="rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary/90"
            >
              Back to Cohorts
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        {/* Compact cohort context header */}
        <div className="flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate("/admin/programmes")}
            className="mb-3 flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-900"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Cohorts
          </button>

          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-gray-900">{programme.name}</h1>
                {programme.is_freemium && <FreemiumBadge />}
              </div>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
                <Calendar className="h-4 w-4" />
                {format(new Date(programme.start_date), "MMM d")} –{" "}
                {format(new Date(programme.end_date), "MMM d, yyyy")} · {data?.total ?? 0} enrolled
                {programme.conversion && (
                  <>
                    {" · "}
                    <ConversionCount conversion={programme.conversion} />
                  </>
                )}
              </p>
            </div>
            <div className="flex flex-shrink-0 items-center gap-2">
              <button
                type="button"
                onClick={handleExportCSV}
                disabled={isExporting}
                className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export CSV
              </button>
              {canEnrol ? (
                <button
                  type="button"
                  onClick={() => setShowAddUserModal(true)}
                  className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
                >
                  <UserPlus className="h-4 w-4" />
                  Add Participant
                  {isSuperadmin && enrollmentClosed && (
                    <span className="rounded bg-white/20 px-1.5 py-0.5 text-xs font-semibold">
                      override
                    </span>
                  )}
                </button>
              ) : (
                <span
                  className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-500"
                  title="Enrollment cutoff has passed; a superadmin can still enroll participants."
                >
                  <UserPlus className="h-4 w-4" />
                  Enrollment closed
                </span>
              )}
            </div>
          </div>
        </div>

        <DataTable
          className="h-full"
          columns={columns}
          data={data?.items ?? []}
          isLoading={enrollmentsQuery.isPending}
          error={error}
          onRetry={() => enrollmentsQuery.refetch()}
          emptyMessage="No participants enrolled yet"
          getRowId={(e) => e.id}
          toolbar={
            <SearchInput
              value={search}
              onChange={(v) => {
                setSearch(v);
                setPage(1);
              }}
              placeholder="Search participants…"
            />
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />

        {/* Add User Modal */}
        {showAddUserModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="max-h-[80vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-200 p-6">
                <h2 className="font-semibold text-gray-900 text-xl">Add Participant to Cohort</h2>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddUserModal(false);
                    setSearchQuery("");
                  }}
                  className="rounded-lg p-2 transition-colors hover:bg-gray-100"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Search */}
              <div className="border-b border-gray-200 p-6">
                <div className="relative">
                  <Search className="-translate-y-1/2 absolute top-1/2 left-3 z-10 h-5 w-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search participants..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>
              </div>

              {/* User List */}
              <div className="max-h-96 overflow-y-auto">
                {filteredAvailableUsers.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                      <Users className="h-8 w-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600">
                      {searchQuery
                        ? "No participants found"
                        : "All participants are already enrolled"}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredAvailableUsers.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 transition-colors hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                            <span className="font-medium text-sm text-green-700">
                              {user.username.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{user.username}</p>
                            <span
                              className={`mt-1 inline-block rounded-full px-2 py-0.5 font-medium text-xs ${getRoleBadgeColor(
                                user.role
                              )}`}
                            >
                              {user.role}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddUser(user.id)}
                          disabled={isAdding}
                          className="rounded-lg bg-primary px-4 py-2 font-medium text-sm text-white transition-colors hover:bg-primary/90 disabled:bg-primary/40"
                        >
                          {isAdding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
